// The month a CX award is paid for. Kim's file reuses the New Car Sales sheet, so the month arrives
// in the column that list uses for a week, written however whoever filled it in chose to write it.
// The same normalising the backend does is done here too, so what the screen shows before sending is
// what ends up stored, and a bad value is caught before the batch leaves the browser.

const MONTH_NAMES = [
     "January",
     "February",
     "March",
     "April",
     "May",
     "June",
     "July",
     "August",
     "September",
     "October",
     "November",
     "December",
];

export const MONTH_PROBLEM_MESSAGE =
     "Month is required and must name a real month, for example August, Aug 2026 or 08/2026.";

const abbreviate = (monthNumber) =>
     MONTH_NAMES[monthNumber - 1].slice(0, 3).toUpperCase();

// A month name or number, then an optional year behind any of the usual separators.
const NAMED_PATTERN = /^([a-z]{3,9})[\s\-_./]*(\d{4})?$/i;

// A numeric month with an optional year on either side, "08/2026" or "2026-08".
const YEAR_FIRST_PATTERN = /^(\d{4})[\s\-_./]+(\d{1,2})$/;
const MONTH_FIRST_PATTERN = /^(\d{1,2})(?:[\s\-_./]+(\d{4}))?$/;

const readMonthName = (name) => {
     const key = name.toLowerCase();

     const index = MONTH_NAMES.findIndex(
          (monthName) =>
               monthName.toLowerCase() === key ||
               monthName.slice(0, 3).toLowerCase() === key,
     );

     return index < 0 ? null : index + 1;
};

/**
 * Reduces a submitted month to its stored label, "AUG 2026". Returns an empty string when the value
 * is missing or does not name a real month. A row that names no year takes the current year,
 * because the file is uploaded in the month it reports on.
 */
export const normaliseMonth = (value) => {
     if (value === null || value === undefined) {
          return "";
     }

     const trimmed = String(value).trim();

     if (!trimmed) {
          return "";
     }

     let monthNumber = null;
     let year = null;

     const yearFirst = trimmed.match(YEAR_FIRST_PATTERN);
     const monthFirst = trimmed.match(MONTH_FIRST_PATTERN);
     const named = trimmed.match(NAMED_PATTERN);

     if (yearFirst) {
          year = Number(yearFirst[1]);
          monthNumber = Number(yearFirst[2]);
     } else if (monthFirst) {
          monthNumber = Number(monthFirst[1]);
          year = monthFirst[2] ? Number(monthFirst[2]) : null;
     } else if (named) {
          monthNumber = readMonthName(named[1]);
          year = named[2] ? Number(named[2]) : null;
     }

     if (!monthNumber || monthNumber < 1 || monthNumber > 12) {
          return "";
     }

     return `${abbreviate(monthNumber)} ${year || new Date().getFullYear()}`;
};

/**
 * The month a sheet is named after, for example "June (May or earlier data)" gives "June 2026".
 * Renault names each tab after the month the awards are paid for and puts the survey period in
 * brackets after it, so only the leading word is read. Empty when the name says no month.
 */
export const monthFromSheetName = (sheetName) => {
     const leading = String(sheetName || "")
          .trim()
          .replace(/^\d+/, "")
          .split(/[\s(]/)[0];

     // Spelled out, because this fills the month box and every row's month cell, and those are read
     // by a person. It is reduced to its stored form again on the way out.
     return monthDisplayName(normaliseMonth(leading));
};

/** Wording used when a month is shown to a person, for example "August 2026". */
export const monthDisplayName = (storedLabel) => {
     const normalised = normaliseMonth(storedLabel);

     if (!normalised) {
          return "";
     }

     const [abbreviated, year] = normalised.split(" ");

     const monthName = MONTH_NAMES.find(
          (name) => name.slice(0, 3).toUpperCase() === abbreviated,
     );

     return monthName ? `${monthName} ${year}` : "";
};
